/**
 * Feeds the Rust sync engine the same credentials the REST/socket layers use.
 * Wired into channelService.connectSocket/disconnectSocket — the single choke
 * point every login path (and app-start token restore) already goes through.
 * Best-effort: sync auth failures must never break login.
 *
 * The signed-in user id travels with the token. The engine binds each per-user
 * scope to it: a `vault:<userId>` task refuses to cycle unless the id matches,
 * because nothing else ties a scope to an identity and on a shared device the
 * previous user's still-registered vault task would otherwise push their item
 * ciphertext AND their keyblock — which carries both Argon2id wraps of their
 * DEK — into the new user's account. The check fails CLOSED, so omitting the id
 * stops vault sync rather than misrouting it.
 */
import { isTauri } from '../utils/tauri';
import { getClientId } from '../utils/clientId';
import { getStorageNamespace, LOCAL_NAMESPACE } from './storageNamespace';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.apiboo.com/api/v1';

/**
 * The last user id seen with a live token. Cached here rather than read from
 * the auth store: authStore imports channelService, which imports this module,
 * and closing that cycle for one field is not worth it. Token refreshes
 * (tokenRefresher.persistTokens) carry no user id and must not blank it out.
 *
 * A module-scope cache alone is NOT enough, which is what the cold-start bug
 * was: this cache starts empty on every launch, and the only call that ever
 * supplies an id (channelService.connectSocket) does not run until
 * authStore.initializeAuth's getUser() resolves. Relaunching with an expired
 * access token fires the interceptor's refresh — i.e. persistTokens →
 * pushSyncAuth(token) with no id — strictly BEFORE that, so the engine read
 * "token present, no id" as a logout and swapped the open database, the
 * account dir and the extension keychain prefix to the signed-out `_local`
 * bucket while the UI was still Alice. `signedOut` below is what separates a
 * cold, never-populated cache from a real, deliberate sign-out.
 */
let lastUserId = null;
/** Set by an explicit `pushSyncAuth(null)`; cleared by any id-carrying push. */
let signedOut = false;

/**
 * The synchronous JS identity carrier — the zustand-persisted `auth-storage`
 * blob, read through the same accessor every namespaced storage key already
 * goes through. It is populated before the first render of a signed-in cold
 * start (persist rehydrates synchronously at module load), which is exactly
 * the window the module cache above cannot cover. Returns null when signed
 * out. Best-effort: never let identity recovery throw into a token refresh.
 */
function persistedUserId() {
  try {
    const ns = getStorageNamespace();
    return ns == null || ns === LOCAL_NAMESPACE ? null : String(ns);
  } catch {
    return null;
  }
}

export function pushSyncAuth(token, userId) {
  if (!isTauri()) return;
  const explicitId = userId == null || String(userId) === '' ? null : String(userId);
  if (!token) {
    // The one call that means "sign out". Must still swap the engine away
    // from the account — do not recover an id here.
    lastUserId = null;
    signedOut = true;
  } else if (explicitId != null) {
    lastUserId = explicitId;
    signedOut = false;
  } else if (lastUserId == null && !signedOut) {
    // Credential update carrying no identity, on a cache that was never
    // populated: a cold start's token refresh. Recover the owner rather than
    // reporting "signed out" for an account that is very much signed in.
    lastUserId = persistedUserId();
  }
  import('@tauri-apps/api/core')
    .then(({ invoke }) =>
      invoke('sync_set_auth', {
        token: token || null,
        baseUrl: token ? BASE_URL : null,
        clientId: token ? getClientId() : null,
        userId: token ? lastUserId : null,
      })
    )
    .catch((err) => console.warn('[sync] sync_set_auth failed:', err));
}

export function clearSyncAuth() {
  pushSyncAuth(null);
}
